import {observeCheckoutHTML} from '../../checkout';
import {type TokenResult} from '../abstract/class-abstract-stripe-payment';
import {billingDetails} from '../stripe';
import {type Stripe, type StripeElements, type StripeCardNumberElement, type StripeCardExpiryElement, type StripeCardCvcElement, type StripeElementChangeEvent} from '@stripe/stripe-js';
import {$qs} from '../../../../@shared/ts/dom';
import {type PeachPayCheckoutData} from '../../types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

/**
 * Renders a custom stripe element.
 */
class StripeCardElement {
	private readonly $cardNumber: StripeCardNumberElement;
	private lastCardNumberChange: StripeElementChangeEvent | null;

	private readonly $cardExpiry: StripeCardExpiryElement;
	private lastCardExpiryChange: StripeElementChangeEvent | null;

	private readonly $cardCvc: StripeCardCvcElement;
	private lastCardCvcChange: StripeElementChangeEvent | null;

	private mounted: boolean;

	constructor(elements: StripeElements) {
		this.mounted = false;

		const style = {
			base: {
				color: '#333',
				fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
				fontSmoothing: 'antialiased',
				fontSize: '16px',
				'::placeholder': {
					color: '#6a6a6a',
				},
			},
		};

		this.$cardNumber = elements.create('cardNumber', {style, placeholder: 'Card number'});
		this.lastCardNumberChange = null;

		this.$cardExpiry = elements.create('cardExpiry', {style});
		this.lastCardExpiryChange = null;

		this.$cardCvc = elements.create('cardCvc', {style, placeholder: 'CVV'});
		this.lastCardCvcChange = null;

		this.init();
	}

	public mount() {
		if (this.mounted) {
			return;
		}

		this.mounted = true;

		observeCheckoutHTML(() => {
			const $container = $qs('#pp-stripe-card-element');
			if (!$container) {
				return;
			}

			if (Array.from($container.querySelectorAll('[class="loading-blur"]')).length === 0) {
				return;
			}

			const $cardNumberElementContainer = $qs('#pp-stripe-card-number');
			const $cardExpiryElementContainer = $qs('#pp-stripe-card-expiry');
			const $cardCvcElementContainer = $qs('#pp-stripe-card-cvc');

			if (!$cardNumberElementContainer || !$cardExpiryElementContainer || !$cardCvcElementContainer) {
				return;
			}

			$cardNumberElementContainer.innerHTML = '';
			$cardExpiryElementContainer.innerHTML = '';
			$cardCvcElementContainer.innerHTML = '';

			this.$cardNumber.mount($cardNumberElementContainer);
			this.$cardExpiry.mount($cardExpiryElementContainer);
			this.$cardCvc.mount($cardCvcElementContainer);
		});

		const $cardNumberElementContainer = $qs('#pp-stripe-card-number');
		const $cardExpiryElementContainer = $qs('#pp-stripe-card-expiry');
		const $cardCvcElementContainer = $qs('#pp-stripe-card-cvc');

		if (!$cardNumberElementContainer || !$cardExpiryElementContainer || !$cardCvcElementContainer) {
			return;
		}

		$cardNumberElementContainer.innerHTML = '';
		$cardExpiryElementContainer.innerHTML = '';
		$cardCvcElementContainer.innerHTML = '';

		this.$cardNumber.mount($cardNumberElementContainer);
		this.$cardExpiry.mount($cardExpiryElementContainer);
		this.$cardCvc.mount($cardCvcElementContainer);
	}

	public async tokenize(stripe: Stripe): Promise<TokenResult> {
		const {paymentMethod, error} = await stripe.createPaymentMethod({
			type: 'card',
			card: this.$cardNumber,
			billing_details: billingDetails(),
		});

		if (error) {
			switch (error.code) {
				case 'incomplete_cvc':
				case 'invalid_cvc':
					this.$cardCvc.focus();
					break;
				case 'incomplete_expiry':
				case 'invalid_expiry_year_past':
					this.$cardExpiry.focus();
					break;
				case 'invalid_number':
				case 'incomplete_number':
				default:
					this.$cardNumber.focus();
					break;
			}

			const message = error.message ?? '';

			// For some reason focusing will cause a change event to fire. We put a timer here as short as possible so
			// this message is not cleared as soon as it is set.
			setTimeout(() => {
				this.elementStatus(message, 'error');
			}, 10);

			return [null, {
				note: message,
			}];
		}

		if (!paymentMethod?.id) {
			return [null, {
				note: 'Failed to tokenize payment method.',
			}];
		}

		return [paymentMethod.id, {}];
	}

	private init() {
		this.$cardNumber.on('focus', this.elementFocusStatus('Enter your card number.'));
		this.$cardNumber.on('change', this.elementChangeHandler('cardnumber', 'Enter your card number.', this.$cardExpiry.focus));
		this.$cardNumber.on('change', e => {
			$qs<HTMLImageElement>('#pp-stripe-card-brand img', $img => {
				$img.src = `${peachpay_checkout_data.plugin.asset_url_base}/img/marks/${(!e?.brand || e.brand === 'unknown') ? 'credit-card-regular' : e.brand}.svg`;
			});
		});
		this.$cardNumber.on('blur', this.elementBlur());

		this.$cardExpiry.on('focus', this.elementFocusStatus('Enter the expiration date.'));
		this.$cardExpiry.on('change', this.elementChangeHandler('cardexpiry', 'Enter the expiration date.', this.$cardCvc.focus));
		this.$cardExpiry.on('blur', this.elementBlur());

		this.$cardCvc.on('focus', this.elementFocusStatus('Enter the security code.'));
		this.$cardCvc.on('change', this.elementChangeHandler('cardcvc', 'Enter the security code.'));
		this.$cardCvc.on('blur', this.elementBlur());
	}

	private elementLastChange(element: 'cardnumber' | 'cardexpiry' | 'cardcvc', event: StripeElementChangeEvent | null) {
		if (element === 'cardnumber') {
			this.lastCardNumberChange = event;
		} else if (element === 'cardexpiry') {
			this.lastCardExpiryChange = event;
		} else if (element === 'cardcvc') {
			this.lastCardCvcChange = event;
		}
	}

	private elementStatus(message: string, type: 'error' | 'info') {
		if (type === 'error') {
			$qs('#pp-stripe-card-element', $el => {
				$el.classList.add('error');
			});
			$qs('#pp-stripe-card-status', $el => {
				$el.classList.add('show');
				$el.classList.add('error');
			});
		} else {
			$qs('#pp-stripe-card-status', $el => {
				$el.classList.add('show');
			});
		}

		$qs('#pp-stripe-card-status span', $el => {
			$el.innerHTML = message;
		});
	}

	private elementFocusStatus(message: string) {
		return () => {
			$qs('#pp-stripe-card-element', $el => {
				$el.classList.add('focused');
			});
			$qs('#pp-stripe-card-status', $el => {
				$el.classList.remove('error');
				$el.classList.add('show');
			});
			$qs('#pp-stripe-card-status span', $el => {
				$el.innerHTML = message;
			});
		};
	}

	private elementBlur() {
		return () => {
			$qs('#pp-stripe-card-element', $el => {
				$el.classList.remove('focused');
				$el.classList.remove('error');
			});
			$qs('#pp-stripe-card-status', $el => {
				$el.classList.remove('show');
				$el.classList.remove('error');
			});
			$qs('#pp-stripe-card-status span', $span => {
				$span.innerHTML = '';
			});

			this.maybeBlurError();
		};
	}

	private maybeBlurError() {
		if (this.lastCardNumberChange) {
			this.elementStatus(this.lastCardNumberChange.error?.message ?? '', 'error');
			return;
		}

		if (this.lastCardExpiryChange) {
			this.elementStatus(this.lastCardExpiryChange.error?.message ?? '', 'error');
			return;
		}

		if (this.lastCardCvcChange) {
			this.elementStatus(this.lastCardCvcChange.error?.message ?? '', 'error');
		}
	}

	private elementChangeHandler(element: 'cardnumber' | 'cardexpiry' | 'cardcvc', defaultMessage: string, focus?: () => void) {
		return (e: StripeElementChangeEvent) => {
			$qs('#pp-stripe-card-element', $el => {
				$el.classList.remove('error');
			});
			$qs('#pp-stripe-card-status', $el => {
				$el.classList.remove('error');
			});
			if (e.error) {
				this.elementLastChange(element, e);
				$qs('#pp-stripe-card-element', $el => {
					$el.classList.add('error');
				});
				$qs('#pp-stripe-card-status', $el => {
					$el.classList.add('show');
					$el.classList.add('error');
				});

				$qs('#pp-stripe-card-status span', $el => {
					$el.innerHTML = e.error?.message ?? '';
				});
			} else if (e.complete) {
				this.elementLastChange(element, null);
				$qs('#pp-stripe-card-status', $el => {
					$el.classList.remove('show');
				});
				$qs('#pp-stripe-card-status span', $el => {
					$el.innerHTML = '';
				});
				focus?.();
			} else {
				this.elementLastChange(element, null);
				$qs('#pp-stripe-card-status', $el => {
					$el.classList.add('show');
				});
				$qs('#pp-stripe-card-status span', $el => {
					$el.innerHTML = defaultMessage;
				});
			}
		};
	}
}

export {
	StripeCardElement,
};
